import { useRef, useState } from "react";
import { api, API_BASE } from "@/lib/api";
import { toast } from "sonner";
import { Upload, Loader2 } from "lucide-react";

// Backend returns { url: "/api/files/..." } which we need to convert to absolute URL
const toAbsolute = (relUrl) => {
  if (!relUrl) return "";
  if (relUrl.startsWith("http")) return relUrl;
  const base = API_BASE.replace(/\/api$/, "");
  const path = relUrl.startsWith("/") ? relUrl : `/${relUrl}`;
  return `${base}${path}`;
};

export default function ImageUploader({ onUploaded, testId = "image-upload" }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFiles = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const r = await api.post("/uploads/image", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        onUploaded(toAbsolute(r.data.url));
      }
      toast.success(`${files.length} image${files.length > 1 ? "s" : ""} uploaded`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={(e) => handleFiles(Array.from(e.target.files || []))}
        data-testid={`${testId}-input`}
      />
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={uploading}
        className="btn-outline text-sm inline-flex items-center gap-2"
        data-testid={`${testId}-button`}
      >
        {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
        {uploading ? "Uploading…" : "Upload Images"}
      </button>
    </div>
  );
}

export { toAbsolute };
